/**
 * Core Commands — Registered by the platform, not hard-coded in the overlay.
 *
 * Each command is a `CommandDefinition` that the overlay discovers
 * via the registry. Plugins add their own commands the same way,
 * using `registerCommand()` from their frontend init hook.
 *
 * ONLY platform-level navigation commands belong here.
 * Plugin-specific commands (create.meeting, create.note, open.todos, etc.)
 * are registered by their owning plugins at boot.
 *
 * Phase 6: Unified command palette exposes both page actions and overlay commands.
 */

import { useOverlayCommandRegistry } from './command-registry';
import { useAuthStore } from '../../stores/authStore';
import { usePermissionRegistry } from '../../plugins/registries/permission-registry';
import type { CommandDefinition } from './command-registry';

/**
 * Platform-level core commands.
 * Plugin-specific commands are registered dynamically — see `registerCommand()`.
 */
const coreCommands: CommandDefinition[] = [
  // ── Core navigation commands ─────────────────────────────────

  {
    id: 'open.dashboard',
    title: 'Go to dashboard',
    description: 'Open the main dashboard',
    group: 'Navigation',
    keywords: ['home', 'dashboard', 'overview'],
    execute: async () => ({ kind: 'navigate', to: '/dashboard' }),
  },

  {
    id: 'open.settings',
    title: 'Open settings',
    description: 'Navigate to settings page',
    group: 'Navigation',
    keywords: ['settings', 'preferences', 'configuration', 'config'],
    execute: async () => ({ kind: 'navigate', to: '/settings' }),
  },

  {
    id: 'open.plugins',
    title: 'Manage plugins',
    description: 'Navigate to plugin management',
    group: 'Navigation',
    keywords: ['plugins', 'extensions', 'integrations'],
    execute: async () => ({ kind: 'navigate', to: '/settings/plugins' }),
  },

  {
    id: 'open.workflows',
    title: 'Open workflows',
    description: 'Navigate to workflow library',
    group: 'Navigation',
    keywords: ['workflows', 'automation', 'tasks'],
    execute: async () => ({ kind: 'navigate', to: '/workflows' }),
  },

  {
    id: 'open.agents',
    title: 'Agent cockpit',
    description: 'Monitor and control agents',
    group: 'Navigation',
    keywords: ['agents', 'automation', 'ai', 'control'],
    execute: async () => ({ kind: 'navigate', to: '/agents/cockpit' }),
  },

  {
    id: 'open.graph',
    title: 'Knowledge graph',
    description: 'Explore the knowledge graph',
    group: 'Navigation',
    keywords: ['graph', 'knowledge', 'entities', 'relationships'],
    execute: async () => ({ kind: 'navigate', to: '/graph' }),
  },

  {
    id: 'open.search',
    title: 'Search console',
    description: 'Search the global index and check its health (admins)',
    group: 'Navigation',
    keywords: ['search', 'retrieval', 'index', 'semantic'],
    execute: async () => ({ kind: 'navigate', to: '/search' }),
  },

  {
    id: 'open.approvals',
    title: 'Approval inbox',
    description: 'Review and resolve pending approvals',
    group: 'Navigation',
    keywords: ['approval', 'inbox', 'review', 'policy'],
    execute: async () => ({ kind: 'navigate', to: '/approvals' }),
  },

  {
    id: 'open.artifacts',
    title: 'Artifact explorer',
    description: 'Browse and inspect artifacts',
    group: 'Navigation',
    keywords: ['artifact', 'assets', 'outputs', 'knowledge'],
    execute: async () => ({ kind: 'navigate', to: '/artifacts' }),
  },

  {
    id: 'open.workflow-runs',
    title: 'Workflow run viewer',
    description: 'Inspect workflow run status and outcomes',
    group: 'Navigation',
    keywords: ['workflow runs', 'run history', 'execution'],
    execute: async () => ({ kind: 'navigate', to: '/workflow-runs' }),
  },

  {
    id: 'open.execution-traces',
    title: 'Execution traces',
    description: 'View low-level execution traces',
    group: 'Navigation',
    keywords: ['trace', 'debug', 'execution', 'telemetry'],
    execute: async () => ({ kind: 'navigate', to: '/execution-traces' }),
  },

  {
    id: 'open.runners',
    title: 'View runners',
    description: 'Manage execution runtimes',
    group: 'Navigation',
    keywords: ['runners', 'execution', 'runtimes'],
    execute: async () => ({ kind: 'navigate', to: '/runners' }),
  },

  {
    id: 'open.system-health',
    title: 'System health',
    description: 'Server checks, plugin errors, live logs and request stats (admins)',
    group: 'Navigation',
    keywords: ['system', 'health', 'devtools', 'debug', 'observability', 'logs', 'plugins', 'errors', 'status'],
    isAvailable: () => useAuthStore.getState().userRole === 'admin' || usePermissionRegistry.getState().isAdmin(),
    execute: async () => ({ kind: 'navigate', to: '/system-health' }),
  },

  // ── System commands ──────────────────────────────────────────

  {
    id: 'system.reload',
    title: 'Reload application',
    description: 'Refresh the browser',
    group: 'System',
    keywords: ['reload', 'refresh', 'restart'],
    execute: async () => {
      window.location.reload();
      return { kind: 'executed' };
    },
  },
];

/**
 * Register all core commands into the overlay command registry.
 * Called once during app initialization (via PluginLoader).
 */
export function registerCoreCommands(): void {
  const { registerCommand } = useOverlayCommandRegistry.getState();
  for (const cmd of coreCommands) {
    registerCommand(cmd);
  }
}

